<template>
  <div class="e_slider">
    <button
      v-if="items.length > 1"
      type="button"
      class="nav_btn prev"
      :disabled="value === 0"
      @click="movePrev()"
    >
      <span class="offscreen">이전</span>
    </button>
    <button
      v-if="items.length > 1"
      type="button"
      class="nav_btn next"
      :disabled="value === maxIndex"
      @click="moveNext()"
    >
      <span class="offscreen">다음</span>
    </button>
    <div
      v-if="items.length > 1"
      class="slider_pagination"
    >
      <button
        v-for="(item, idx) in items"
        :key="'page' + idx"
        type="button"
        :class="['page_bullet', { on: idx === value }]"
        @click="$emit('input', idx)"
      >{{ idx + 1 }}</button>
    </div>
    <!-- peek 영역은 음수 마진으로 레이아웃 폭에서 제외 : 현재 슬라이드가 중앙 기준을 유지 -->
    <div
      class="slide_viewport"
      :style="{
        width: (viewportWidth + leftPeekExtra + rightPeekExtra) + 'px',
        marginLeft: -leftPeekExtra + 'px',
        marginRight: -rightPeekExtra + 'px',
        height: viewportHeight ? viewportHeight + 'px' : 'auto'
      }"
    >
      <div
        ref="track"
        class="slide_track"
        :style="{ gap: gap + 'px', transform: 'translateX(' + (-slideOffset) + 'px)' }"
      >
        <div
          v-for="(item, idx) in items"
          :key="'slide' + idx"
          ref="slideItems"
          :class="['slide_item', { on: idx === value }]"
          :style="{ flex: '0 0 ' + slideWidths[idx] + 'px' }"
          @click="idx !== value && $emit('input', idx)"
        >
          <!-- 슬라이드 내용물은 사용하는 쪽에서 채움 -->
          <slot
            :item="item"
            :index="idx"
          />
        </div>
      </div>
    </div>
    <!-- 뷰포트 아래 고정 영역(닫기 바 등) : 현재 슬라이드 폭에 맞춰짐 -->
    <slot name="footer" />
  </div>
</template>

<script>
const DEFAULT_SLIDE_WIDTH = 500

export default {
  name: 'ESlider',
  props: {
    // 슬라이드 데이터 배열
    items: {
      type: Array,
      required: true
    },
    // 슬라이드별 폭(px) 배열. 항목이 없거나 값이 비면 500
    widths: {
      type: Array,
      default: function () {
        return []
      }
    },
    // 현재 슬라이드 인덱스 (v-model)
    value: {
      type: Number,
      default: 0
    },
    // 슬라이드 사이 간격(px)
    gap: {
      type: Number,
      default: 20
    },
    // 이전 슬라이드 미리보기(peek) 폭(px). 0이면 사용 안 함
    leftPeek: {
      type: Number,
      default: 0
    },
    // 다음 슬라이드 미리보기(peek) 폭(px). 0이면 사용 안 함
    rightPeek: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      // 현재 슬라이드의 실측 높이 : 뷰포트(=화살표 기준 영역)가 현재 슬라이드에 맞춰지도록
      viewportHeight: 0
    }
  },
  computed: {
    maxIndex () {
      return this.items.length - 1
    },
    slideWidths () {
      return this.items.map((item, idx) => Number(this.widths[idx]) || DEFAULT_SLIDE_WIDTH)
    },
    slideOffset () {
      const offset = this.slideWidths
        .slice(0, this.value)
        .reduce((sum, w) => sum + w + this.gap, 0)

      // 왼쪽 peek 만큼 덜 밀어서 이전 슬라이드 가장자리가 보이게 함
      return offset - this.leftPeekExtra
    },
    // 한 번에 한 장씩 노출 : 현재 슬라이드 폭이 곧 뷰포트 폭
    viewportWidth () {
      return this.slideWidths[this.value] || DEFAULT_SLIDE_WIDTH
    },
    // peek 사용 시 이전/다음 슬라이드 가장자리가 보이도록 뷰포트에 더해줄 폭
    leftPeekExtra () {
      return this.leftPeek > 0 && this.value > 0 ? this.gap + this.leftPeek : 0
    },
    rightPeekExtra () {
      return this.rightPeek > 0 && this.value < this.maxIndex ? this.gap + this.rightPeek : 0
    }
  },
  watch: {
    value () {
      this.$nextTick(this.updateViewportHeight)
    },
    items () {
      this.$nextTick(this.observeSlides)
    }
  },
  mounted () {
    this.observeSlides()
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateViewportHeight)
    }
  },
  methods: {
    // 슬라이드 내용 변화(이미지 로드 등)에 따른 높이 재계산을 위해 슬라이드 각각을 관찰
    observeSlides () {
      this.updateViewportHeight()

      if (typeof ResizeObserver === 'undefined') {
        window.removeEventListener('resize', this.updateViewportHeight)
        window.addEventListener('resize', this.updateViewportHeight)
        return
      }

      if (!this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(this.updateViewportHeight)
      } else {
        this.resizeObserver.disconnect()
      }

      const slides = this.$refs.slideItems || []
      slides.forEach(el => this.resizeObserver.observe(el))
    },
    // 현재 슬라이드의 높이를 재서 뷰포트에 반영
    updateViewportHeight () {
      const slides = this.$refs.slideItems

      if (slides && slides[this.value]) {
        this.viewportHeight = slides[this.value].offsetHeight
      }
    },
    movePrev () {
      if (this.value > 0) {
        this.$emit('input', this.value - 1)
      }
    },
    moveNext () {
      if (this.value < this.maxIndex) {
        this.$emit('input', this.value + 1)
      }
    }
  }
}
</script>

<style scoped>
.e_slider {
  position: relative;
}
.nav_btn {
  position: absolute;
  top: 50%;
  margin-top: -22px;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  z-index: 1;
}
.nav_btn::before {
  content: '';
  display: inline-block;
  width: 12px;
  height: 12px;
  border-top: 2px solid #ffffff;
  border-right: 2px solid #ffffff;
}
.nav_btn.prev {
  left: -58px;
}
.nav_btn.prev::before {
  transform: rotate(-135deg);
  margin-left: 5px;
}
.nav_btn.next {
  right: -58px;
}
.nav_btn.next::before {
  transform: rotate(45deg);
  margin-right: 5px;
}
.nav_btn:disabled {
  opacity: 0.3;
  cursor: default;
}
/* 숫자 페이지네이션 : absolute로 빼서 슬라이드가 많아져도 본체 폭(현재 슬라이드 폭)에 영향을 주지 않고,
   슬라이드 폭을 넘어가면 여러 줄로 위쪽에 쌓임 */
.slider_pagination {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-bottom: 10px;
  z-index: 1;
}
.page_bullet {
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  border: 0;
  border-radius: 13px;
  background-color: rgba(255, 255, 255, 0.75);
  color: #222222;
  font-size: 13px;
  cursor: pointer;
}
.page_bullet.on {
  background-color: #0641b8;
  color: #ffffff;
  font-weight: 600;
}
.slide_viewport {
  position: relative;
  overflow: hidden;
  /* 현재 슬라이드에 맞춰 폭/높이/peek 여백 모두 부드럽게 전환 */
  transition: width 0.3s ease, height 0.3s ease, margin-left 0.3s ease, margin-right 0.3s ease;
}
.slide_track {
  display: flex;
  /* 슬라이드마다 내용만큼의 높이를 갖도록 stretch 하지 않음 */
  align-items: flex-start;
  transition: transform 0.3s ease;
}
/* peek로 살짝 보이는(현재가 아닌) 슬라이드는 흐리게 처리, 클릭하면 해당 슬라이드로 이동 */
.slide_item {
  transition: opacity 0.3s ease;
}
.slide_item:not(.on) {
  opacity: 0.45;
  cursor: pointer;
}
</style>
